/**
 * useAdminInboxCounts — 어드민 인박스(고객 이벤트) 카운트 폴링 훅
 *
 * 반환 카운트:
 *   - unread       : 안 읽은 알림 (전 타입)
 *   - unresolved   : 미처리 customer_event (실제 "처리 대기 할 일" 카운트)
 *   - byEventKey   : 미처리 카운트의 event_key 별 분포
 *   - bySeverity   : 미처리 카운트의 severity 별 분포
 *
 * 즉각성 메커니즘 (HR useBadgeCounts 와 같다) — 모든 신호는 탭 공용 예약기(utils/sharedFetcher)를 거친다:
 *   1) 폴링 — 기본 20초 (포커스 시), 백그라운드에서 자동 중단
 *   2) Visibility/Focus/Online — 사용자 복귀 즉시 refresh
 *   3) BroadcastChannel('admin-inbox') — 같은 브라우저 다른 탭의 처리 액션 즉시 반영
 *   4) SSE (/api/notifications/stream) — 새 알림/읽음/처리완료 push 즉시 반영
 *
 * 사이드바·헤더 벨·대시보드 위젯이 동시에 같은 훅을 사용해도 됩니다 (낮은 빈도, 가벼운 응답).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import notificationService, { InboxCounts, subscribeNotificationStream } from '../services/notifications';
import { authScope, createSharedFetcher } from '../utils/sharedFetcher';

const DEFAULT: InboxCounts = {
  unread: 0,
  unresolved: 0,
  byEventKey: {},
  bySeverity: { info: 0, warning: 0, critical: 0 },
};

const CHANNEL_NAME = 'admin-inbox';

// 탭 안의 모든 장치(사이드바·대시보드)가 한 예약기를 쓴다 — 신호가 몰려도 2초에 한 번(utils/sharedFetcher)
export const inboxCountsFetcher = createSharedFetcher<InboxCounts>(() => notificationService.getInboxCounts(), 2_000, authScope);

export function useAdminInboxCounts(intervalMs = 20_000) {
  const [counts, setCounts] = useState<InboxCounts>(() => inboxCountsFetcher.getLatest() ?? DEFAULT);
  const intervalIdRef = useRef<number | null>(null);

  const startPolling = useCallback(() => {
    if (intervalIdRef.current != null) return;
    intervalIdRef.current = window.setInterval(() => inboxCountsFetcher.request(), intervalMs);
  }, [intervalMs]);

  const stopPolling = useCallback(() => {
    if (intervalIdRef.current != null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = inboxCountsFetcher.subscribe(setCounts);
    inboxCountsFetcher.request();
    startPolling();

    // 창 복귀·온라인 복귀도 같은 예약기로(장치 수만큼 동시에 묻지 않는다)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        inboxCountsFetcher.request();
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const onFocus = () => inboxCountsFetcher.request();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);

    // BroadcastChannel — 같은 브라우저 다른 탭의 «처리» 동작(broadcastInboxAction) 즉시 반영
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel(CHANNEL_NAME);
        bc.onmessage = (e) => {
          if (e?.data?.type === 'refresh') inboxCountsFetcher.request();
        };
      }
    } catch { /* unsupported */ }

    // SSE — 공유 단일 연결 구독 (탭당 EventSource 1개 — 연결 한도 소진 방지)
    //   ⛔ 여기서 다른 탭에 다시 알리지 않는다 — 탭마다 같은 신호를 이미 받는다(알리면 탭·훅 수의 제곱으로 불어난다)
    const unsubscribeStream = subscribeNotificationStream(() => inboxCountsFetcher.request());

    return () => {
      unsubscribe();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
      if (bc) bc.close();
      unsubscribeStream();
    };
  }, [startPolling, stopPolling]);

  return { counts, refresh: inboxCountsFetcher.fetchNow };
}

/**
 * 처리완료/벌크 처리 후 모든 탭에 즉시 refresh 신호 전파.
 */
export function broadcastInboxAction(): void {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel(CHANNEL_NAME);
      bc.postMessage({ type: 'refresh', at: Date.now() });
      bc.close();
    }
  } catch { /* unsupported */ }
}
